import IDButton from '../ui/IDButton';
import { DotsVerticalRounded } from '@boxicons/react';
import { useNavigate } from 'react-router';
import DemonFace from './DemonFace';
import { DemonLogoSizes } from '../../utils/difficultyToImgSrc';
import YesTick from '../images/YesTick';
import { IDMapper } from '../../utils/IDMapper';
import { useApp } from '../../context/app/useApp';
import type { Difficulty, Rarity } from '@gdladder/shared/enums';

interface Props {
    ID: number;
    difficulty?: Difficulty;
    rarity?: Rarity;
    rating: number | null;
    defaultRating?: number | null;
    actualRating?: number | null;
    enjoyment: number | null;
    actualEnjoyment?: number | null;
    tooltip?: string;
    completed?: boolean;
    name: string;
    creator?: string | null;
    songName?: string;
    onContextMenu?: React.MouseEventHandler;
    selected?: boolean;
    position?: number;
}

export default function Level({
    ID,
    difficulty,
    rarity,
    rating,
    defaultRating,
    actualRating,
    enjoyment,
    actualEnjoyment,
    tooltip,
    name,
    creator,
    position,
    songName,
    completed = false,
    onContextMenu,
    selected = false,
}: Props) {
    const roundedTier = Math.round(rating ?? defaultRating ?? 0);
    const roundedEnjoyment = enjoyment !== null ? Math.round(enjoyment) : -1;

    const navigate = useNavigate();
    const app = useApp();
    const isCompletedHighlighted = completed && app.highlightCompleted;

    return (
        <div
            className={
                'relative group cursor-pointer overflow-hidden border outline-offset-4 round:rounded-lg slow-effect-transition focus-visible:outline-2' +
                (isCompletedHighlighted && !app.enableLevelThumbnails
                    ? ' bg-green-900 border-green-700 hover:bg-green-800 hover:border-green-500'
                    : ' bg-theme-700 border-theme-outline hover:bg-theme-600 hover:border-theme-400') +
                (selected ? ' outline-2 ring-2 ring-blue-400 z-20' : '')
            }
            onClick={() => void navigate('/level/' + ID)}
            onContextMenu={onContextMenu}
            tabIndex={0}
            title={tooltip}
        >
            {app.enableLevelThumbnails && (
                <img
                    className='absolute size-full object-cover object-center transition-all opacity-60 group-hover:opacity-100 focus-visible:opacity-100 level-thumbnail'
                    loading='lazy'
                    src={`https://levelthumbs.prevter.me/thumbnail/${IDMapper(ID)}`}
                    style={{ maskImage: 'linear-gradient(to right, transparent var(--mask-start-at, 40%), black 90%)' }}
                />
            )}
            <div className='relative z-9 flex min-h-20 items-center justify-between gap-3 px-3 py-2 max-sm:flex-wrap'>
                <div className='flex min-w-0 flex-1 gap-3 items-center'>
                    {difficulty !== undefined && (
                        <DemonFace diff={difficulty} rarity={rarity} size={DemonLogoSizes.SMALL} />
                    )}
                    <div className='min-w-0 flex flex-col'>
                        <p className='text-lg lg:text-xl wrap-anywhere'>
                            <span className='inline-flex items-center gap-2'>
                                <b>{name}</b>
                                {isCompletedHighlighted && <YesTick className='shrink-0 size-6 md:hidden' />}
                            </span>
                            <br className='md:hidden' />
                            {creator && <span> by {creator}</span>}
                            {isCompletedHighlighted && (
                                <YesTick className='hidden md:inline-block align-middle ms-2 size-6' />
                            )}
                        </p>
                        {position !== undefined && <p className='text-lg text-gray-400'>Queue: #{position}</p>}
                    </div>
                </div>
                <div className='flex shrink-0 items-center gap-3'>
                    <div className='self-center hidden lg:block'>
                        <div
                            className='flex flex-col text-end'
                            style={{ textShadow: '1px 2px 0px black, 0px 1px 10px black' }}
                        >
                            {ID > 3 && (
                                <p>
                                    ID:{' '}
                                    <IDButton id={ID} style={{ textShadow: '1px 2px 0px black, 0px 1px 10px black' }} />
                                </p>
                            )}
                            {songName && (
                                <p className='max-w-72 truncate' title={songName}>
                                    song: <b>{songName}</b>
                                </p>
                            )}
                        </div>
                    </div>
                    <div className='flex gap-2 text-shadow-lg'>
                        <div
                            className={`grid h-10 w-13 lg:w-20 place-items-center round:rounded-md font-bold text-lg lg:text-xl tier-${roundedTier}`}
                        >
                            <p className='group-hover:hidden cursor-default'>
                                {rating !== null ? roundedTier : (defaultRating ?? '-')}
                            </p>
                            <p className='hidden group-hover:block cursor-default'>
                                {(actualRating ?? rating)?.toFixed(2) ?? '-'}
                            </p>
                        </div>
                        <div
                            className={`grid h-10 w-13 lg:w-20 place-items-center round:rounded-md font-bold text-lg lg:text-xl enj-${roundedEnjoyment}`}
                        >
                            <p className='group-hover:hidden cursor-default'>
                                {enjoyment !== null ? roundedEnjoyment : '-'}
                            </p>
                            <p className='hidden group-hover:block cursor-default'>
                                {(actualEnjoyment ?? enjoyment)?.toFixed(2) ?? '-'}
                            </p>
                        </div>
                    </div>
                    {onContextMenu && (
                        <button
                            type='button'
                            className='-mx-2 p-0.5 rounded-full hover:bg-white/20 transition-colors'
                            onClick={(e) => {
                                e.stopPropagation();
                                onContextMenu(e);
                            }}
                        >
                            <DotsVerticalRounded pack='filled' size='sm' />
                        </button>
                    )}
                </div>
            </div>
        </div>
    );
}

export function LevelSkeleton() {
    return (
        <div className='flex min-h-20 items-center justify-between gap-3 border border-theme-outline bg-theme-700 px-3 py-2 round:rounded-lg'>
            <div className='flex min-w-0 flex-1 gap-3 items-center'>
                <span className='size-16 bg-theme-500 rounded-full' />
                <span className='h-6 w-full max-w-64 bg-theme-500 round:rounded shimmer' />
            </div>
            <div className='flex shrink-0 gap-3'>
                <div className='self-center hidden lg:block'>
                    <div
                        className='flex flex-col gap-2'
                        style={{ textShadow: '1px 2px 0px black, 0px 1px 10px black' }}
                    >
                        <span className='h-4 w-20 ms-auto bg-theme-500 round:rounded shimmer' />
                        <span className='h-4 w-40 bg-theme-500 round:rounded shimmer' />
                    </div>
                </div>
                <div className='flex gap-2'>
                    <div className='h-10 w-13 lg:w-20 bg-theme-500 round:rounded-md shimmer' />
                    <div className='h-10 w-13 lg:w-20 bg-theme-500 round:rounded-md shimmer' />
                </div>
            </div>
        </div>
    );
}
